import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { usePostUomMutation } from 'api';
import { PostUnitOfMeasureRequest } from 'api/bindings/PostUnitOfMeasureRequest';
import { AppInput, FormButtonGroup } from 'features';
import { useCallback } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface CreateUnitOfMeasurementDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'isOpen' | 'children'> {
  disallowedUomNames?: string[];
}

interface Form extends PostUnitOfMeasureRequest {}

export const CreateUnitOfMeasurementDrawer = (
  props: CreateUnitOfMeasurementDrawerProps
) => {
  const { onClose, disallowedUomNames = [], ...rest } = props;

  // Fetches
  const [postUom] = usePostUomMutation();

  // State
  const toast = useToast();
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {},
  });

  const { watch, control, handleSubmit, reset } = form;
  const name = watch('name');

  const isValidName = useCallback(
    (name) => {
      return (
        (disallowedUomNames.some(
          (x) => x.toLowerCase() === name.toLowerCase()
        ) &&
          `Unit of measurement ${name} is already taken.`) ||
        undefined
      );
    },
    [disallowedUomNames]
  );

  const onSubmit = async (form: Form) => {
    try {
      const result = await postUom(form);
      if ('error' in result) {
        toast({
          title: `Failed to post unit of measure: ${form.name}`,
          status: 'error',
        });
        return;
      }

      toast({
        title: `Unit of measure ${form.name} posted!`,
        status: 'success',
      });
      reset();
      onClose();
    } catch {}
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Drawer isOpen={true} onClose={onClose} size="xl">
          <DrawerOverlay />
          <DrawerCloseButton />
          <DrawerContent>
            <DrawerHeader>New Unit of Measure: {name}</DrawerHeader>
            <DrawerBody>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="name">Name:</FormLabel>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: true,
                      minLength: {
                        value: 3,
                        message: 'Name must be at least 3 characters.',
                      },
                      maxLength: {
                        value: 256,
                        message: 'Name must be at most 256 characters.',
                      },
                      validate: { isValidName },
                    }}
                    render={(f) => <AppInput id="name" {...(f.field as any)} />}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="abbrev">Abbreviation:</FormLabel>
                  <Controller
                    name="abbreviation"
                    control={control}
                    rules={{
                      required: true,
                      minLength: {
                        value: 1,
                        message: `Abbreviation must be at least one character.`,
                      },
                      maxLength: {
                        value: 32,
                        message: `Abbreviation must be at most 32 characters.`,
                      },
                    }}
                    render={(f) => (
                      <AppInput id="abbrev" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onConfirm={handleSubmit(onSubmit)}
                onCancel={() => {
                  reset();
                  onClose();
                }}
              />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </FormProvider>
  );
};
